import { IPatientDetails } from "../types/PrescriptionIngestionTypes";

export interface IFieldErrors {
    [key: string]: string | undefined;
}

export class ValidationUtils {
    /**
     * Validates patient details form
     */
    static validatePatientDetails(patient: IPatientDetails): {
        isValid: boolean;
        errors: string[];
        fieldErrors: IFieldErrors;
    } {
        const errors: string[] = [];
        const fieldErrors: IFieldErrors = {};

        if (!patient.firstName?.trim()) {
            const error = "Patient first name is required";
            errors.push(error);
            fieldErrors.firstName = error;
        }

        if (!patient.lastName?.trim()) {
            const error = "Patient last name is required";
            errors.push(error);
            fieldErrors.lastName = error;
        }

        if (!patient.dateOfBirth?.trim()) {
            const error = "Patient date of birth is required";
            errors.push(error);
            fieldErrors.dateOfBirth = error;
        } else if (!this.isValidDate(patient.dateOfBirth)) {
            const error = "Patient date of birth must be in YYYY-MM-DD format";
            errors.push(error);
            fieldErrors.dateOfBirth = error;
        }

        if (!patient.gender?.trim()) {
            const error = "Patient gender is required";
            errors.push(error);
            fieldErrors.gender = error;
        }

        if (!patient.addressLine1?.trim()) {
            const error = "Patient address line 1 is required";
            errors.push(error);
            fieldErrors.addressLine1 = error;
        }

        if (!patient.addressLine2?.trim()) {
            const error = "Patient address line 2 is required";
            errors.push(error);
            fieldErrors.addressLine2 = error;
        }

        if (!patient.city?.trim()) {
            const error = "Patient city is required";
            errors.push(error);
            fieldErrors.city = error;
        }

        if (!patient.state?.trim()) {
            const error = "Patient state is required";
            errors.push(error);
            fieldErrors.state = error;
        }

        if (!patient.zipCode?.trim()) {
            const error = "Patient zip code is required";
            errors.push(error);
            fieldErrors.zipCode = error;
        }

        if (!patient.phoneNumber?.trim()) {
            const error = "Patient phone number is required";
            errors.push(error);
            fieldErrors.phoneNumber = error;
        } else if (!this.isValidPhoneNumber(patient.phoneNumber)) {
            const error = "Patient phone number must be 10 digits";
            errors.push(error);
            fieldErrors.phoneNumber = error;
        }

        return {
            isValid: errors.length === 0,
            errors,
            fieldErrors
        };
    }

    /**
     * Validates date format (YYYY-MM-DD)
     */
    private static isValidDate(dateString: string): boolean {
        // Check basic format first
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateString)) {
            return false;
        }

        // Parse the date components
        const [year, month, day] = dateString.split('-').map(Number);

        // Basic range checks
        if (year < 1900 || year > 2100) return false;
        if (month < 1 || month > 12) return false;
        if (day < 1 || day > 31) return false;

        const date = new Date(year, month - 1, day);

        return date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day &&
            !isNaN(date.getTime());
    }

    /**
     * Validates phone number (10 digits)
     */
    private static isValidPhoneNumber(phoneNumber: string): boolean {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phoneNumber.replace(/\D/g, ''));
    }

    /**
     * Validates JSON string
     */
    static validateJSON(jsonString: string): { isValid: boolean; error?: string } {
        try {
            JSON.parse(jsonString);
            return { isValid: true };
        } catch (error) {
            return {
                isValid: false,
                error: error instanceof Error ? error.message : 'Invalid JSON format'
            };
        }
    }

    /**
     * Validates source pharmacy details for TransferRx
     */
    static validateSourcePharmacy(pharmacy: {
        ncpdp: string;
        npi: string;
        businessName: string;
        addressLine1: string;
        city: string;
        state: string;
        zipCode: string;
    }): {
        isValid: boolean;
        errors: string[];
        fieldErrors: IFieldErrors;
    } {
        const errors: string[] = [];
        const fieldErrors: IFieldErrors = {};

        if (!pharmacy.ncpdp?.trim()) {
            const error = "NCPDP ID is required";
            errors.push(error);
            fieldErrors.sourcePharmacyNCPDP = error;
        } else if (!/^\d{7}$/.test(pharmacy.ncpdp.trim())) {
            const error = "NCPDP ID must be 7 digits";
            errors.push(error);
            fieldErrors.sourcePharmacyNCPDP = error;
        }

        if (!pharmacy.npi?.trim()) {
            const error = "NPI is required";
            errors.push(error);
            fieldErrors.sourcePharmacyNPI = error;
        } else if (!/^\d{10}$/.test(pharmacy.npi.trim())) {
            const error = "NPI must be 10 digits";
            errors.push(error);
            fieldErrors.sourcePharmacyNPI = error;
        }

        if (!pharmacy.businessName?.trim()) {
            const error = "Business name is required";
            errors.push(error);
            fieldErrors.sourcePharmacyBusinessName = error;
        } else if (pharmacy.businessName.trim().length < 3) {
            const error = "Business name must be at least 3 characters";
            errors.push(error);
            fieldErrors.sourcePharmacyBusinessName = error;
        }

        if (!pharmacy.addressLine1?.trim()) {
            const error = "Address is required";
            errors.push(error);
            fieldErrors.sourcePharmacyAddressLine1 = error;
        }

        if (!pharmacy.city?.trim()) {
            const error = "City is required";
            errors.push(error);
            fieldErrors.sourcePharmacyCity = error;
        }

        if (!pharmacy.state?.trim()) {
            const error = "State is required";
            errors.push(error);
            fieldErrors.sourcePharmacyState = error;
        }

        if (!pharmacy.zipCode?.trim()) {
            const error = "Zip code is required";
            errors.push(error);
            fieldErrors.sourcePharmacyZipCode = error;
        }

        return {
            isValid: errors.length === 0,
            errors,
            fieldErrors
        };
    }

    /**
     * Validates medication details
     */
    static validateMedicationDetails(medication: {
        daysSupply: string;
        ndc: string;
        sigText: string;
        quantity: { value: string };
        writtenDate: string;
    }): {
        isValid: boolean;
        errors: string[];
        fieldErrors: IFieldErrors;
    } {
        const errors: string[] = [];
        const fieldErrors: IFieldErrors = {};

        // Days Supply validation
        if (!medication.daysSupply?.trim()) {
            const error = "Days supply is required";
            errors.push(error);
            fieldErrors.daysSupply = error;
        } else {
            const days = parseInt(medication.daysSupply);
            if (isNaN(days) || days < 1 || days > 365) {
                const error = "Days supply must be between 1 and 365";
                errors.push(error);
                fieldErrors.daysSupply = error;
            }
        }

        // NDC validation
        if (!medication.ndc?.trim()) {
            const error = "NDC is required";
            errors.push(error);
            fieldErrors.ndc = error;
        }

        // Sig Text validation
        if (!medication.sigText?.trim()) {
            const error = "Instructions (Sig Text) are required";
            errors.push(error);
            fieldErrors.sigText = error;
        }

        // Quantity validation
        if (!medication.quantity?.value?.trim()) {
            const error = "Quantity is required";
            errors.push(error);
            fieldErrors.quantity = error;
        } else {
            const qty = parseFloat(medication.quantity.value);
            if (isNaN(qty) || qty < 1 || qty > 9999) {
                const error = "Quantity must be between 1 and 9999";
                errors.push(error);
                fieldErrors.quantity = error;
            }
        }

        // Written Date validation
        if (!medication.writtenDate?.trim()) {
            const error = "Written date is required";
            errors.push(error);
            fieldErrors.writtenDate = error;
        } else if (!this.isValidDate(medication.writtenDate)) {
            const error = "Written date must be a valid date";
            errors.push(error);
            fieldErrors.writtenDate = error;
        } else if (new Date(medication.writtenDate) > new Date()) {
            const error = "Written date cannot be in the future";
            errors.push(error);
            fieldErrors.writtenDate = error;
        }

        return {
            isValid: errors.length === 0,
            errors,
            fieldErrors
        };
    }

    /**
     * Validates transfer details for TransferRx
     */
    static validateTransferDetails(transfer: {
        refillsRemaining: string;
        quantityRemaining: string;
        quantityTransferred: string;
        lastFillDate: string;
        writtenDate: string;
        expirationDate: string;
        medicationRefills?: number;
    }): {
        isValid: boolean;
        errors: string[];
        fieldErrors: IFieldErrors;
    } {
        const errors: string[] = [];
        const fieldErrors: IFieldErrors = {};

        // Refills Remaining
        if (!transfer.refillsRemaining?.trim()) {
            const error = "Refills remaining is required";
            errors.push(error);
            fieldErrors.refillsRemaining = error;
        } else {
            const refills = parseInt(transfer.refillsRemaining);
            if (isNaN(refills) || refills < 0) {
                const error = "Refills remaining must be 0 or greater";
                errors.push(error);
                fieldErrors.refillsRemaining = error;
            } else if (transfer.medicationRefills !== undefined && refills > transfer.medicationRefills) {
                const error = `Refills remaining must be less than or equal to medication's total refills (${transfer.medicationRefills})`;
                errors.push(error);
                fieldErrors.refillsRemaining = error;
            }
        }

        // Quantity Remaining
        if (!transfer.quantityRemaining?.trim()) {
            const error = "Quantity remaining is required";
            errors.push(error);
            fieldErrors.quantityRemaining = error;
        } else {
            const qty = parseFloat(transfer.quantityRemaining);
            if (isNaN(qty) || qty <= 0) {
                const error = "Quantity remaining must be greater than 0";
                errors.push(error);
                fieldErrors.quantityRemaining = error;
            }
        }

        // Quantity Transferred
        if (!transfer.quantityTransferred?.trim()) {
            const error = "Quantity transferred is required";
            errors.push(error);
            fieldErrors.quantityTransferred = error;
        } else {
            const qty = parseFloat(transfer.quantityTransferred);
            if (isNaN(qty) || qty <= 0) {
                const error = "Quantity transferred must be greater than 0";
                errors.push(error);
                fieldErrors.quantityTransferred = error;
            }
        }

        // Last Fill Date
        if (!transfer.lastFillDate?.trim()) {
            const error = "Last fill date is required";
            errors.push(error);
            fieldErrors.lastFillDate = error;
        } else if (!this.isValidDate(transfer.lastFillDate)) {
            const error = "Last fill date must be a valid date";
            errors.push(error);
            fieldErrors.lastFillDate = error;
        } else if (new Date(transfer.lastFillDate) > new Date()) {
            const error = "Last fill date cannot be in the future";
            errors.push(error);
            fieldErrors.lastFillDate = error;
        }

        // Written Date
        if (!transfer.writtenDate?.trim()) {
            const error = "Written date is required";
            errors.push(error);
            fieldErrors.writtenDate = error;
        } else if (!this.isValidDate(transfer.writtenDate)) {
            const error = "Written date must be a valid date";
            errors.push(error);
            fieldErrors.writtenDate = error;
        } else if (new Date(transfer.writtenDate) > new Date()) {
            const error = "Written date cannot be in the future";
            errors.push(error);
            fieldErrors.writtenDate = error;
        }

        // Expiration Date
        if (!transfer.expirationDate?.trim()) {
            const error = "Expiration date is required";
            errors.push(error);
            fieldErrors.expirationDate = error;
        } else if (!this.isValidDate(transfer.expirationDate)) {
            const error = "Expiration date must be a valid date";
            errors.push(error);
            fieldErrors.expirationDate = error;
        } else if (new Date(transfer.expirationDate) < new Date()) {
            const error = "Expiration date must be in the future";
            errors.push(error);
            fieldErrors.expirationDate = error;
        }

        return {
            isValid: errors.length === 0,
            errors,
            fieldErrors
        };
    }
}